import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Upload, FileText, Loader2, Sparkles, AlertCircle, 
  CheckCircle2, Calendar, Euro, Building2, ListChecks, Download, Eye,
  MapPin, Clock, AlertTriangle, Award, Users, FileCheck,
  Globe, Phone, Mail, Gavel, Percent, Truck
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { pdfjs } from "react-pdf";
import "@/lib/pdfConfig"; // Initialize PDF.js worker
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";

export interface KeyInfo {
  market_type: "travaux" | "services" | "fournitures" | null;
  market_form: string | null;
  procedure_type: string | null;
  lots_count: number | null;
  location: string | null;
  duration: string | null;
  site_visit: {
    required: boolean;
    date: string | null;
    time: string | null;
    location: string | null;
  } | null;
  qualifications_required: string[] | null;
  min_turnover: number | null;
  min_references: number | null;
  execution_delay: string | null;
  late_penalties: string | null;
  submission_method: string | null;
  submission_platform: string | null;
  offer_validity_days: number | null;
  variants_allowed: boolean | null;
  subcontracting_allowed: boolean | null;
  advance_payment: string | null;
  payment_delay_days: number | null;
  negotiation_planned: boolean | null;
  buyer_contact_email: string | null;
  buyer_contact_phone: string | null;
}

export interface AnalysisResult {
  summary: string;
  deadline: string | null;
  budget: number | null;
  buyer_name: string | null;
  requirements: Array<{
    label: string;
    category: "administrative" | "technical" | "financial" | "reference";
    mandatory: boolean;
  }>;
  criteria: Array<{
    name: string;
    weight: number | null;
  }>;
  key_info?: KeyInfo;
}

interface DCEUploadAnalyzerProps {
  tenderId: string;
  tenderTitle: string;
  userId: string;
  existingDcePath?: string | null;
  existingDceDocumentId?: string | null;
  onAnalysisComplete: (result: AnalysisResult, dcePath: string, dceDocumentId: string) => void;
}

type AnalysisStep = "idle" | "uploading" | "extracting" | "analyzing" | "complete" | "error";

export function DCEUploadAnalyzer({ 
  tenderId, 
  tenderTitle, 
  userId,
  existingDcePath,
  existingDceDocumentId,
  onAnalysisComplete 
}: DCEUploadAnalyzerProps) {
  const [step, setStep] = useState<AnalysisStep>("idle");
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { openDocument } = useDocumentPreview();

  // Upload file to storage AND create document record with bucket_id = 'tender-dce'
  const uploadDCEFile = async (file: File): Promise<{ filePath: string; documentId: string }> => {
    const filePath = `${userId}/${tenderId}/${file.name}`;
    
    const { error: uploadError } = await supabase.storage
      .from("tender-dce")
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      throw new Error("Erreur lors de l'upload du fichier: " + uploadError.message);
    }

    // Create document record in documents table with bucket_id = 'tender-dce'
    const { data: docData, error: docError } = await supabase
      .from("documents")
      .insert({
        user_id: userId,
        title: `DCE - ${tenderTitle}`,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: "application/pdf",
        doc_type: "other" as const,
        source: "upload" as const,
        status: "complete" as const,
        category: "administrative" as const,
        bucket_id: "tender-dce",
      })
      .select("id")
      .single();

    if (docError || !docData) {
      throw new Error("Erreur lors de la création du document: " + (docError?.message || "Unknown error"));
    }

    return { filePath, documentId: docData.id };
  };

  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = "";
    const totalPages = pdf.numPages;
    
    for (let i = 1; i <= totalPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(" ");
      fullText += pageText + "\n\n";
      
      // Update progress (extraction is 20-60%)
      setProgress(20 + Math.round((i / totalPages) * 40));
    }
    
    return fullText;
  };

  const analyzeWithAI = async (text: string): Promise<AnalysisResult> => {
    const { data, error } = await supabase.functions.invoke("analyze-tender", {
      body: { documentText: text, title: tenderTitle }
    });

    if (error) {
      throw new Error(error.message || "Erreur lors de l'analyse IA");
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data.analysis;
  };

  const processFile = async (file: File) => {
    setFileName(file.name);
    setError(null);
    setProgress(0);

    try {
      // Step 1: Upload file to storage + create document record
      setStep("uploading");
      setProgress(5);
      const { filePath: dcePath, documentId: dceDocumentId } = await uploadDCEFile(file);
      setProgress(15);

      // Step 2: Extract text from PDF
      setStep("extracting");
      const text = await extractTextFromPDF(file);
      
      if (text.length < 100) {
        throw new Error("Le document semble vide ou illisible. Essayez un autre fichier.");
      }

      // Step 3: Analyze with AI
      setStep("analyzing");
      setProgress(70);
      
      const result = await analyzeWithAI(text);
      
      setProgress(100);
      setStep("complete");
      
      onAnalysisComplete(result, dcePath, dceDocumentId);
      toast.success("Analyse terminée !");
      
    } catch (err) {
      console.error("DCE analysis error:", err);
      setStep("error");
      setError(err instanceof Error ? err.message : "Erreur inconnue");
      toast.error("Échec de l'analyse");
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      processFile(file);
    }
  }, [tenderTitle, userId, tenderId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"]
    },
    maxFiles: 1,
    disabled: step === "uploading" || step === "extracting" || step === "analyzing"
  });

  const reset = () => {
    setStep("idle");
    setProgress(0);
    setFileName(null);
    setError(null);
  };

  const downloadDCE = async () => {
    if (!existingDcePath) return;
    
    const { data, error } = await supabase.storage
      .from("tender-dce")
      .download(existingDcePath);

    if (error) {
      toast.error("Erreur lors du téléchargement");
      return;
    }

    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = existingDcePath.split("/").pop() || "dce.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // View DCE using standard DocumentPreviewSheet
  const viewDCE = async () => {
    if (!existingDceDocumentId) return;
    
    try {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", existingDceDocumentId)
        .single();

      if (error) throw error;
      if (data) {
        openDocument(data);
      }
    } catch (err) {
      console.error("Error viewing DCE:", err);
      toast.error("Impossible d'ouvrir le document");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          Analyse IA du DCE
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Show existing DCE buttons */}
        {existingDcePath && step === "idle" && (
          <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">
                {existingDcePath.split("/").pop()}
              </span>
            </div>
            <div className="flex gap-2">
              {existingDceDocumentId && (
                <Button variant="outline" size="sm" onClick={viewDCE}>
                  <Eye className="w-4 h-4 mr-1.5" />
                  Voir
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={downloadDCE}>
                <Download className="w-4 h-4 mr-1.5" />
                Télécharger
              </Button>
            </div>
          </div>
        )}

        {step === "idle" && (
          <div
            {...getRootProps()}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
              isDragActive 
                ? "border-primary bg-primary/5" 
                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
            )}
          >
            <input {...getInputProps()} />
            <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">
              {isDragActive ? "Déposez le DCE ici" : existingDcePath ? "Remplacer le DCE" : "Glissez votre DCE (PDF)"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              L'IA extraira les pièces à fournir et les critères
            </p>
          </div>
        )}

        {(step === "uploading" || step === "extracting" || step === "analyzing") && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{fileName}</p>
                <p className="text-xs text-muted-foreground">
                  {step === "uploading" 
                    ? "Upload du fichier..." 
                    : step === "extracting" 
                    ? "Extraction du texte..." 
                    : "Analyse IA en cours..."}
                </p>
              </div>
              <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-center text-muted-foreground">
              {step === "uploading"
                ? "Sauvegarde du document..."
                : step === "extracting" 
                ? "Lecture des pages du document..."
                : "L'IA identifie les pièces et critères..."
              }
            </p>
          </div>
        )}

        {step === "complete" && (
          <div className="text-center py-4">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
            <p className="text-sm font-medium text-emerald-600">Analyse terminée</p>
            <p className="text-xs text-muted-foreground mt-1">
              Les informations ont été extraites du DCE
            </p>
            <Button variant="outline" size="sm" onClick={reset} className="mt-3">
              Analyser un autre document
            </Button>
          </div>
        )}

        {step === "error" && (
          <div className="text-center py-4">
            <AlertCircle className="w-10 h-10 mx-auto text-destructive mb-2" />
            <p className="text-sm font-medium text-destructive">Échec de l'analyse</p>
            <p className="text-xs text-muted-foreground mt-1">{error}</p>
            <Button variant="outline" size="sm" onClick={reset} className="mt-3">
              Réessayer
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Component to display the analysis result
interface AnalysisResultDisplayProps {
  result: AnalysisResult;
}

export function AnalysisResultDisplay({ result }: AnalysisResultDisplayProps) {
  const categoryLabels: Record<string, string> = {
    administrative: "Administratif",
    technical: "Technique",
    financial: "Financier",
    reference: "Références"
  };

  const categoryColors: Record<string, string> = {
    administrative: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    technical: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    financial: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    reference: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
  };

  const marketTypeLabels: Record<string, string> = {
    travaux: "Travaux",
    services: "Services",
    fournitures: "Fournitures"
  };

  const keyInfo = result.key_info;

  // Check if we have any alerts to show
  const hasAlerts = keyInfo && (
    keyInfo.site_visit?.required ||
    (keyInfo.qualifications_required && keyInfo.qualifications_required.length > 0) ||
    keyInfo.min_turnover ||
    keyInfo.min_references ||
    keyInfo.execution_delay ||
    keyInfo.late_penalties
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      {result.summary && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">{result.summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Quick Overview - GO/NO-GO */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            🎯 Aperçu rapide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {keyInfo?.market_type && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <FileCheck className="w-3.5 h-3.5" />
                  Type
                </div>
                <p className="font-medium text-sm">{marketTypeLabels[keyInfo.market_type] || keyInfo.market_type}</p>
              </div>
            )}
            
            {keyInfo?.market_form && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Gavel className="w-3.5 h-3.5" />
                  Forme
                </div>
                <p className="font-medium text-sm truncate">{keyInfo.market_form}</p>
              </div>
            )}

            {keyInfo?.procedure_type && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  Procédure
                </div>
                <p className="font-medium text-sm truncate">{keyInfo.procedure_type}</p>
              </div>
            )}

            {keyInfo?.lots_count && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <ListChecks className="w-3.5 h-3.5" />
                  Lots
                </div>
                <p className="font-medium text-sm">{keyInfo.lots_count} lot{keyInfo.lots_count > 1 ? 's' : ''}</p>
              </div>
            )}

            {keyInfo?.location && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <MapPin className="w-3.5 h-3.5" />
                  Lieu
                </div>
                <p className="font-medium text-sm truncate">{keyInfo.location}</p>
              </div>
            )}

            {keyInfo?.duration && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  Durée
                </div>
                <p className="font-medium text-sm">{keyInfo.duration}</p>
              </div>
            )}

            {result.budget && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Euro className="w-3.5 h-3.5" />
                  Budget
                </div>
                <p className="font-medium text-sm">
                  {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(result.budget)}
                </p>
              </div>
            )}

            {result.deadline && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Date limite
                </div>
                <p className="font-medium text-sm">{result.deadline}</p>
              </div>
            )}
          </div>

          {result.buyer_name && (
            <div className="mt-3 pt-3 border-t flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Acheteur:</span>
              <span className="text-sm font-medium">{result.buyer_name}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Constraint Alerts */}
      {hasAlerts && (
        <Card className="border-amber-200 dark:border-amber-900/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              Alertes contraintes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {keyInfo?.site_visit?.required && (
              <div className="flex items-start gap-2 p-2 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-900/50">
                <Badge variant="destructive" className="shrink-0 text-xs">Obligatoire</Badge>
                <div className="text-sm">
                  <span className="font-medium">Visite de site</span>
                  {keyInfo.site_visit.date && (
                    <span className="text-muted-foreground">
                      {" "}le {keyInfo.site_visit.date}
                      {keyInfo.site_visit.time && ` à ${keyInfo.site_visit.time}`}
                    </span>
                  )}
                  {keyInfo.site_visit.location && (
                    <span className="text-muted-foreground block text-xs mt-0.5">
                      📍 {keyInfo.site_visit.location}
                    </span>
                  )}
                </div>
              </div>
            )}

            {keyInfo?.qualifications_required && keyInfo.qualifications_required.length > 0 && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <Award className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">Qualifications requises: </span>
                  <span className="text-muted-foreground">{keyInfo.qualifications_required.join(", ")}</span>
                </div>
              </div>
            )}

            {keyInfo?.min_turnover && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <Euro className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">CA minimum exigé: </span>
                  <span className="text-muted-foreground">
                    {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(keyInfo.min_turnover)}
                  </span>
                </div>
              </div>
            )}

            {keyInfo?.min_references && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <FileText className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">Références exigées: </span>
                  <span className="text-muted-foreground">{keyInfo.min_references} référence{keyInfo.min_references > 1 ? 's' : ''} similaire{keyInfo.min_references > 1 ? 's' : ''}</span>
                </div>
              </div>
            )}

            {keyInfo?.execution_delay && (
              <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                <Clock className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">Délai d'exécution: </span>
                  <span className="text-muted-foreground">{keyInfo.execution_delay}</span>
                </div>
              </div>
            )}

            {keyInfo?.late_penalties && (
              <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                <AlertCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">Pénalités de retard: </span>
                  <span className="text-muted-foreground">{keyInfo.late_penalties}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Market Details */}
      {keyInfo && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              📝 Détails du marché
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {keyInfo.submission_method && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" />
                    Remise des offres
                  </span>
                  <span className="font-medium text-right">{keyInfo.submission_method}</span>
                </div>
              )}

              {keyInfo.offer_validity_days && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Validité offres
                  </span>
                  <span className="font-medium">{keyInfo.offer_validity_days} jours</span>
                </div>
              )}

              {keyInfo.variants_allowed !== null && keyInfo.variants_allowed !== undefined && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <FileCheck className="w-3.5 h-3.5" />
                    Variantes
                  </span>
                  <Badge variant={keyInfo.variants_allowed ? "default" : "secondary"}>
                    {keyInfo.variants_allowed ? "Autorisées" : "Non autorisées"}
                  </Badge>
                </div>
              )}

              {keyInfo.subcontracting_allowed !== null && keyInfo.subcontracting_allowed !== undefined && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    Sous-traitance
                  </span>
                  <Badge variant={keyInfo.subcontracting_allowed ? "default" : "secondary"}>
                    {keyInfo.subcontracting_allowed ? "Autorisée" : "Non autorisée"}
                  </Badge>
                </div>
              )}

              {keyInfo.advance_payment && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Percent className="w-3.5 h-3.5" />
                    Avances
                  </span>
                  <span className="font-medium">{keyInfo.advance_payment}</span>
                </div>
              )}

              {keyInfo.payment_delay_days && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Euro className="w-3.5 h-3.5" />
                    Délai paiement
                  </span>
                  <span className="font-medium">{keyInfo.payment_delay_days} jours</span>
                </div>
              )}

              {keyInfo.negotiation_planned !== null && keyInfo.negotiation_planned !== undefined && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Gavel className="w-3.5 h-3.5" />
                    Négociation
                  </span>
                  <Badge variant={keyInfo.negotiation_planned ? "default" : "secondary"}>
                    {keyInfo.negotiation_planned ? "Prévue" : "Non prévue"}
                  </Badge>
                </div>
              )}

              {keyInfo.submission_platform && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded col-span-full">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5" />
                    Plateforme
                  </span>
                  <a 
                    href={keyInfo.submission_platform.startsWith('http') ? keyInfo.submission_platform : `https://${keyInfo.submission_platform}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline truncate max-w-[60%]"
                  >
                    {keyInfo.submission_platform}
                  </a>
                </div>
              )}
            </div>

            {/* Contact info */}
            {(keyInfo.buyer_contact_email || keyInfo.buyer_contact_phone) && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-2">Contact acheteur</p>
                <div className="flex flex-wrap gap-3">
                  {keyInfo.buyer_contact_email && (
                    <a 
                      href={`mailto:${keyInfo.buyer_contact_email}`}
                      className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {keyInfo.buyer_contact_email}
                    </a>
                  )}
                  {keyInfo.buyer_contact_phone && (
                    <a 
                      href={`tel:${keyInfo.buyer_contact_phone}`}
                      className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {keyInfo.buyer_contact_phone}
                    </a>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Requirements grouped by category */}
      {result.requirements && result.requirements.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-4 h-4" />
              Pièces à fournir ({result.requirements.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(["administrative", "technical", "financial", "reference"] as const).map(category => {
              const items = result.requirements.filter(r => r.category === category);
              if (items.length === 0) return null;
              
              return (
                <div key={category}>
                  <p className={cn(
                    "text-xs font-medium px-2 py-1 rounded-md inline-block mb-2",
                    categoryColors[category]
                  )}>
                    {categoryLabels[category]} ({items.length})
                  </p>
                  <ul className="space-y-1">
                    {items.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className={cn(
                          "shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full",
                          req.mandatory ? "bg-destructive" : "bg-muted-foreground/50"
                        )} />
                        <span className={cn(!req.mandatory && "text-muted-foreground")}>
                          {req.label}
                          {req.mandatory && <span className="text-destructive text-xs ml-1">*</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Criteria */}
      {result.criteria && result.criteria.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Critères de jugement</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {result.criteria.map((criterion, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span>{criterion.name}</span>
                  {criterion.weight && (
                    <span className="text-muted-foreground font-medium">{criterion.weight}%</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
